import { z } from 'zod';

/** Statement batch status from Prisma `StatementBatchStatus` (wire uses string). */
export const statementBatchStatusSchema = z.enum([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'PARTIAL_SUCCESS',
  'FAILED',
] as const);

/**
 * Org-scoped owner statement batch row.
 * `summaryJson` stays permissive — backend aggregate shape evolves independently of UI.
 */
export const statementBatchRowSchema = z.object({
  id: z.string(),
  operatingOrganizationId: z.string(),
  yyyyMm: z.string(),
  status: statementBatchStatusSchema,
  summaryJson: z.unknown().optional().nullable(),
  triggeredByUserId: z.string().nullable().optional(),
  startedAt: z.string(),
  finishedAt: z.string().nullable().optional(),
});

export const statementBatchListResponseSchema = z.object({
  batches: z.array(statementBatchRowSchema),
});

/** Permissive summary persisted on batch rows and execute responses. */
export const statementBatchSummaryJsonSchema = z.object({
  totalCount: z.number().optional(),
  total: z.number().optional(),
  successCount: z.number().optional(),
  failureCount: z.number().optional(),
  cachedSuccessCount: z.number().optional(),
  failures: z
    .array(
      z.object({
        propertyId: z.string(),
        ownerId: z.string().optional(),
        reason: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
  errors: z
    .array(
      z.object({
        propertyId: z.string(),
        error: z.string(),
        code: z.string().optional(),
      }),
    )
    .optional(),
});

/** POST /accounting/statement-batches */
export const statementBatchCreateResponseSchema = z.object({
  batch: statementBatchRowSchema,
});

/** POST /accounting/statement-batches/:id/execute */
export const statementBatchExecuteResponseSchema = z.object({
  batchId: z.string(),
  status: statementBatchStatusSchema,
  summary: statementBatchSummaryJsonSchema,
});

export type StatementBatchRow = z.infer<typeof statementBatchRowSchema>;
export type StatementBatchListResponse = z.infer<typeof statementBatchListResponseSchema>;
